/**
 * Resolves the PHYSICAL_INVENTORY_ID that an AMI008 export must be named after.
 *
 * The identifier is the inventory's title. willChange_import_import_PALM parses it out of the
 * AMI004 file name and puts it there, and willImport_item_item_flair carries it onto the inventory
 * record, so for an inventory imported from a conforming file name the title *is* the identifier.
 *
 * The preferred source is `navigationSelection.record`, because that is the same inventory
 * doingExport_namespace_relation scopes each item's `inventory` array to
 * (filterInventoryByNavigationSelection). Taking the name from anywhere else leaves the name and
 * the rows provably independent, even where they happen to coincide today.
 *
 * Returns either an identifier or a message to show the user - it does not throw, because a thrown
 * error never reaches the user from this rule: the workflow's failure event sink swallows
 * UserErrors, so the caller has to notify explicitly.
 *
 * Known limitation: the title is only an identifier when the import file name conformed. An import
 * whose file name did not follow the convention is deliberately allowed to proceed and keeps the
 * title generated by willNew_import_import, and inventories created before this naming change
 * carry titles of their own. Naming an AMI008 after one of those produces a file PALM cannot
 * correlate. Reading the identifier back from the import record's persisted `fileName` would settle
 * it, but `import:import` is excluded from the generic get rules
 * (CRUD/namespace/relation/notHandledHere.js) so no prerequisite can load it, and nothing about a
 * title's shape can disqualify it because PALM does not constrain the identifier format. The
 * durable fix is to stop producing such an inventory - to reject a non-conforming AMI004 file name
 * at import time - which is a product decision rather than one to make here.
 *
 * Deliberately unsupported: an export run after the inventory was closed. Closing a PALM inventory
 * pulls the inventory association off every item (closeInventory.removeInventoryFromItems) and
 * willUpdate_inventory_inventory_FLAIR then purges item:item for the tenant, so there is neither an
 * active inventory nor an item left to attribute the export to - and nothing to export either.
 *
 * @param {object} sources
 * @param {{record?: {_id?: string, title?: string}}} [sources.navigationSelection] the inventory
 *  the export is scoped to
 * @param {object[]} [sources.activeInventories] inventory records with status Active
 * @param {object[]} [sources.items] the item records being exported
 * @returns {{physicalInventoryId?: string, error?: string}}
 */
export default function resolvePhysicalInventoryId({ navigationSelection, activeInventories = [], items = [] }) {
    // When the export is scoped to one inventory, that inventory decides the name. Falling back to
    // any other source here would name the file after an inventory whose rows it does not contain.
    const scopedTo = navigationSelection?.record;
    if (scopedTo) return named(scopedTo);

    const inventories = activeInventories.length ? activeInventories : getInventoriesFromItems(items);
    const distinctInventories = distinctById(inventories);

    // The item lookup is tenant-wide, so the rows in the file can span every inventory represented
    // above. Ambiguity is therefore decided on how many inventories are in play, before any single
    // one is considered - naming the file after one of several would mislabel the rest.
    if (distinctInventories.length > 1) {
        // Deliberately no instruction to close one of them. Closing any PALM inventory runs
        // willUpdate_inventory_inventory_FLAIR, whose application reset sets `'item:item': true`
        // and purges the tenant's items - not just the closed inventory's - so "close all but one"
        // would destroy the records belonging to the inventory the user meant to keep. More than
        // one Active inventory is a state the server refuses to create, so recovering from it
        // belongs with an administrator rather than in a self-service instruction here.
        const titles = distinctInventories.map(inventory => inventory.title || '(untitled)').join(', ');
        return {
            error:
                `Unable to export AMI008. ${distinctInventories.length} physical inventories are in play ` +
                `(${titles}), so the file cannot be named unambiguously. Resolve the duplicate active ` +
                'inventories before trying again.'
        };
    }

    return named(distinctInventories[0]);
}

/**
 * @param {{title?: string}} [inventory]
 * @returns {{physicalInventoryId?: string, error?: string}}
 */
function named(inventory) {
    if (inventory?.title) return { physicalInventoryId: inventory.title };
    return {
        error:
            'Unable to export AMI008. No physical inventory was found to name the file after. The export has ' +
            'to run against an inventory created by an AMI004 import.'
    };
}

/**
 * Used only when the export is not scoped to an inventory and none is Active - the window between
 * the import creating the inventory and the last batch flipping it to Active. An item's `inventory`
 * array can hold an association for more than one inventory, and the item lookup returns every
 * non-deleted item, so every association present is collected rather than the first one found.
 * Items dropped from an inventory carry `inventory:excludedInventory` instead and contribute
 * nothing.
 *
 * @param {object[]} items
 * @returns {object[]} the `inventory:inventory` references found on the items
 */
function getInventoriesFromItems(items) {
    const inventories = [];
    for (const item of items) {
        for (const inventory of item.inventory ?? []) {
            if (inventory['inventory:inventory']) inventories.push(inventory['inventory:inventory']);
        }
    }
    return inventories;
}

/**
 * `_id` reaches the client as a string on some paths and an ObjectId-like value on others, so
 * records are keyed by the string form. A record with no `_id` cannot be told apart from another,
 * so it is kept as its own entry rather than silently merged.
 *
 * @param {object[]} records
 * @returns {object[]} one record per distinct `_id`, order preserved
 */
function distinctById(records) {
    const byId = new Map();
    records.forEach((record, index) => {
        const key = record?._id == null ? `no-id-${index}` : String(record._id);
        if (!byId.has(key)) byId.set(key, record);
    });
    return [...byId.values()];
}
